import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  UserIcon,
  CalendarIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  PauseIcon,
  AdjustmentsHorizontalIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  EyeIcon,
  PlusIcon
} from '@heroicons/react/24/outline';

const ApprovalWorkflow = () => {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    // Simulate fetching workflow data
    const fetchWorkflows = () => {
      const mockWorkflows = [
        {
          id: 'WF001',
          title: 'Patient Registration Form Approval',
          formTitle: 'Patient Registration Form v2.1',
          formId: 1,
          type: 'form_approval',
          status: 'in_progress',
          priority: 'high',
          initiatedBy: {
            name: 'Dr. Sarah Johnson',
            avatar: 'https://ui-avatars.com/api/?name=Sarah+Johnson&background=3b82f6&color=fff'
          },
          createdDate: '2025-01-22T10:30:00Z',
          dueDate: '2025-01-25T17:00:00Z',
          currentStep: 2,
          totalSteps: 3,
          steps: [
            {
              id: 1,
              name: 'Department Review',
              assignee: 'Jennifer Wu',
              status: 'completed',
              completedDate: '2025-01-22T14:15:00Z',
              comments: 'Form structure looks good. Minor formatting adjustments made.'
            },
            {
              id: 2,
              name: 'Medical Director Approval',
              assignee: 'Dr. Michael Chen',
              status: 'in_progress',
              assignedDate: '2025-01-22T14:20:00Z',
              comments: null
            },
            {
              id: 3,
              name: 'Final Publication',
              assignee: 'System Admin',
              status: 'pending',
              assignedDate: null,
              comments: null
            }
          ],
          description: 'Approval workflow for updated patient registration form with new medical history fields'
        },
        {
          id: 'WF002',
          title: 'Incident Report Form Review',
          formTitle: 'Incident Report Form v3.0',
          formId: 2,
          type: 'form_review',
          status: 'completed',
          priority: 'medium',
          initiatedBy: {
            name: 'Lisa Rodriguez',
            avatar: 'https://ui-avatars.com/api/?name=Lisa+Rodriguez&background=10b981&color=fff'
          },
          createdDate: '2025-01-20T09:15:00Z',
          dueDate: '2025-01-22T17:00:00Z',
          completedDate: '2025-01-21T16:30:00Z',
          currentStep: 2,
          totalSteps: 2,
          steps: [
            {
              id: 1,
              name: 'Safety Officer Review',
              assignee: 'David Kim',
              status: 'completed',
              completedDate: '2025-01-21T11:20:00Z',
              comments: 'Added additional safety protocol questions.'
            },
            {
              id: 2,
              name: 'Manager Approval',
              assignee: 'Jennifer Wu',
              status: 'completed',
              completedDate: '2025-01-21T16:30:00Z',
              comments: 'Approved for immediate deployment.'
            }
          ],
          description: 'Review process for updated incident reporting form with enhanced safety fields'
        },
        {
          id: 'WF003',
          title: 'Employee Survey Approval',
          formTitle: 'Annual Employee Satisfaction Survey',
          formId: 8,
          type: 'survey_approval',
          status: 'rejected',
          priority: 'low',
          initiatedBy: {
            name: 'Jennifer Wu',
            avatar: 'https://ui-avatars.com/api/?name=Jennifer+Wu&background=f59e0b&color=fff'
          },
          createdDate: '2025-01-19T14:00:00Z',
          dueDate: '2025-01-23T17:00:00Z',
          rejectedDate: '2025-01-20T10:45:00Z',
          currentStep: 1,
          totalSteps: 2,
          steps: [
            {
              id: 1,
              name: 'HR Director Review',
              assignee: 'Dr. Emily Davis',
              status: 'rejected',
              completedDate: '2025-01-20T10:45:00Z',
              comments: 'Survey questions need to be more specific. Please revise and resubmit.'
            },
            {
              id: 2,
              name: 'Executive Approval',
              assignee: 'CEO Office',
              status: 'pending',
              assignedDate: null,
              comments: null
            }
          ],
          description: 'Approval process for annual employee satisfaction survey'
        },
        {
          id: 'WF004',
          title: 'Clinical Trial Form Validation',
          formTitle: 'Clinical Trial Consent Form v1.3',
          formId: 5,
          type: 'validation',
          status: 'pending',
          priority: 'high',
          initiatedBy: {
            name: 'Dr. Emily Davis',
            avatar: 'https://ui-avatars.com/api/?name=Emily+Davis&background=ef4444&color=fff'
          },
          createdDate: '2025-01-21T16:20:00Z',
          dueDate: '2025-01-24T12:00:00Z',
          currentStep: 1,
          totalSteps: 4,
          steps: [
            {
              id: 1,
              name: 'Legal Review',
              assignee: 'Legal Department',
              status: 'pending',
              assignedDate: '2025-01-21T16:25:00Z',
              comments: null
            },
            {
              id: 2,
              name: 'Ethics Committee Review',
              assignee: 'Ethics Board',
              status: 'pending',
              assignedDate: null,
              comments: null
            },
            {
              id: 3,
              name: 'Medical Director Approval',
              assignee: 'Dr. Michael Chen',
              status: 'pending',
              assignedDate: null,
              comments: null
            },
            {
              id: 4,
              name: 'Final Certification',
              assignee: 'Regulatory Affairs',
              status: 'pending',
              assignedDate: null,
              comments: null
            }
          ],
          description: 'Multi-stage validation process for clinical trial consent form'
        }
      ];

      setWorkflows(mockWorkflows);
      setLoading(false);
    };

    setTimeout(fetchWorkflows, 500);
  }, []);

  const getWorkflowsByTab = () => {
    switch (activeTab) {
      case 'active':
        return workflows.filter(wf => ['in_progress', 'pending'].includes(wf.status));
      case 'completed':
        return workflows.filter(wf => wf.status === 'completed');
      case 'rejected':
        return workflows.filter(wf => wf.status === 'rejected');
      default:
        return workflows;
    }
  };

  const filteredWorkflows = getWorkflowsByTab().filter(workflow => {
    const matchesSearch = workflow.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         workflow.formTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         workflow.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || workflow.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || workflow.priority === priorityFilter;

    return matchesSearch && matchesStatus && matchesPriority;
  });

  const sortedWorkflows = [...filteredWorkflows].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'title':
        comparison = a.title.localeCompare(b.title);
        break;
      case 'priority':
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        comparison = priorityOrder[a.priority] - priorityOrder[b.priority];
        break;
      case 'dueDate':
        comparison = new Date(a.dueDate) - new Date(b.dueDate);
        break;
      default: // created
        comparison = new Date(a.createdDate) - new Date(b.createdDate);
    }
    
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const statusColors = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      paused: 'bg-gray-100 text-gray-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <ClockIcon className="h-4 w-4" />;
      case 'in_progress':
        return <ArrowPathIcon className="h-4 w-4 animate-spin" />;
      case 'completed':
        return <CheckCircleIcon className="h-4 w-4" />;
      case 'rejected':
        return <XCircleIcon className="h-4 w-4" />;
      case 'paused':
        return <PauseIcon className="h-4 w-4" />;
      default:
        return <ClockIcon className="h-4 w-4" />;
    }
  };

  const getPriorityColor = (priority) => {
    const priorityColors = {
      high: 'bg-red-100 text-red-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-green-100 text-green-800'
    };
    return priorityColors[priority] || 'bg-gray-100 text-gray-800';
  };

  const getStepStatusColor = (status) => {
    const statusColors = {
      pending: 'bg-gray-100 text-gray-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const tabs = [
    { id: 'all', name: 'All Workflows', count: workflows.length },
    { id: 'active', name: 'Active', count: workflows.filter(wf => ['in_progress', 'pending'].includes(wf.status)).length },
    { id: 'completed', name: 'Completed', count: workflows.filter(wf => wf.status === 'completed').length },
    { id: 'rejected', name: 'Rejected', count: workflows.filter(wf => wf.status === 'rejected').length }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Approval Workflows</h1>
                <p className="mt-2 text-sm text-gray-700">
                  Manage form approval and review processes
                </p>
              </div>
              <div className="mt-4 sm:mt-0">
                <Link
                  to="/forms/workflows/create"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <PlusIcon className="-ml-1 mr-2 h-4 w-4" />
                  Create Workflow
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ArrowPathIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Active Workflows</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {workflows.filter(wf => ['in_progress', 'pending'].includes(wf.status)).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Completed</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {workflows.filter(wf => wf.status === 'completed').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">High Priority</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {workflows.filter(wf => wf.priority === 'high' && ['in_progress', 'pending'].includes(wf.status)).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-8 w-8 text-orange-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Overdue</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {workflows.filter(wf => 
                      new Date(wf.dueDate) < new Date() && 
                      ['in_progress', 'pending'].includes(wf.status)
                    ).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                >
                  {tab.name}
                  <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                    activeTab === tab.id ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            {/* Search */}
            <div className="flex-1 max-w-lg">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Search workflows..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center space-x-4">
              {/* Filters Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <AdjustmentsHorizontalIcon className="h-4 w-4 mr-2" />
                Filters
              </button>

              {/* Sort */}
              <div className="relative">
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setSortBy(field);
                    setSortOrder(order);
                  }}
                  className="block appearance-none bg-white border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm leading-5 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="created-desc">Recently Created</option>
                  <option value="created-asc">Oldest First</option>
                  <option value="dueDate-asc">Due Date</option>
                  <option value="priority-desc">High Priority First</option>
                  <option value="title-asc">Title A-Z</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <ChevronDownIcon className="h-4 w-4" />
                </div>
              </div>
            </div>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority
                  </label>
                  <select
                    value={priorityFilter}
                    onChange={(e) => setPriorityFilter(e.target.value)}
                    className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Priorities</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>

                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setStatusFilter('all');
                      setPriorityFilter('all');
                      setSearchTerm('');
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Workflows List */}
        {sortedWorkflows.length === 0 ? (
          <div className="text-center py-12">
            <ArrowPathIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No workflows found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter !== 'all' || priorityFilter !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by creating your first workflow.'
              }
            </p>
            {!searchTerm && statusFilter === 'all' && priorityFilter === 'all' && (
              <div className="mt-6">
                <Link
                  to="/forms/workflows/create"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <PlusIcon className="-ml-1 mr-2 h-4 w-4" />
                  Create Your First Workflow
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {sortedWorkflows.map((workflow) => (
              <div key={workflow.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow duration-200">
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-start flex-1">
                      <DocumentTextIcon className="h-8 w-8 text-blue-500 mt-1" />
                      <div className="ml-4 flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-medium text-gray-900">{workflow.title}</h3>
                            <p className="text-sm text-gray-600 mt-1">{workflow.description}</p>
                            <Link
                              to={`/forms/${workflow.formId}`}
                              className="text-sm text-blue-600 hover:text-blue-800 mt-2 inline-block"
                            >
                              {workflow.formTitle} →
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(workflow.priority)}`}>
                        {workflow.priority.toUpperCase()}
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(workflow.status)}`}>
                        {getStatusIcon(workflow.status)}
                        <span className="ml-1 capitalize">{workflow.status.replace('_', ' ')}</span>
                      </span>
                    </div>
                  </div>

                  {/* Workflow Progress */}
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        Progress: Step {workflow.currentStep} of {workflow.totalSteps}
                      </span>
                      <span className="text-sm text-gray-500">
                        {Math.round((workflow.currentStep / workflow.totalSteps) * 100)}% Complete
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${(workflow.currentStep / workflow.totalSteps) * 100}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Steps */}
                  <div className="mt-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Workflow Steps</h4>
                    <div className="space-y-3">
                      {workflow.steps.map((step, index) => (
                        <div key={step.id} className="flex items-start">
                          <div className="flex items-center">
                            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                              step.status === 'completed' ? 'bg-green-100 text-green-800' :
                              step.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                              step.status === 'rejected' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {step.status === 'completed' ? (
                                <CheckCircleIcon className="h-4 w-4" />
                              ) : step.status === 'in_progress' ? (
                                <ArrowPathIcon className="h-4 w-4" />
                              ) : step.status === 'rejected' ? (
                                <XCircleIcon className="h-4 w-4" />
                              ) : (
                                index + 1
                              )}
                            </div>
                          </div>
                          
                          <div className="ml-3 flex-1">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-gray-900">{step.name}</p>
                                <p className="text-sm text-gray-500">Assigned to: {step.assignee}</p>
                              </div>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStepStatusColor(step.status)}`}>
                                {step.status.replace('_', ' ')}
                              </span>
                            </div>
                            
                            {step.comments && (
                              <div className="mt-2 p-2 bg-gray-50 rounded text-sm text-gray-700">
                                <div className="flex items-start">
                                  <ChatBubbleLeftRightIcon className="h-4 w-4 mt-0.5 mr-2 text-gray-400" />
                                  {step.comments}
                                </div>
                              </div>
                            )}
                            
                            {step.completedDate && (
                              <p className="text-xs text-gray-500 mt-1">
                                Completed: {formatDate(step.completedDate)}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="mt-6 pt-4 border-t border-gray-200 flex items-center justify-between">
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center">
                        <UserIcon className="h-4 w-4 mr-1" />
                        {workflow.initiatedBy.name}
                      </div>
                      <div className="flex items-center">
                        <CalendarIcon className="h-4 w-4 mr-1" />
                        Created: {formatDate(workflow.createdDate)}
                      </div>
                      <div className="flex items-center">
                        <ClockIcon className="h-4 w-4 mr-1" />
                        Due: {formatDate(workflow.dueDate)}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Link
                        to={`/forms/workflows/${workflow.id}`}
                        className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <EyeIcon className="h-3 w-3 mr-1" />
                        View Details
                      </Link>
                      
                      {workflow.status === 'in_progress' && (
                        <button className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50">
                          <PauseIcon className="h-3 w-3 mr-1" />
                          Pause
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ApprovalWorkflow;
